import * as THREE from 'three';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
// Named exports (`retarget`/`retargetClip`/`clone`), not a `SkeletonUtils`
// namespace object - verified against the module's own .js/.d.ts.
import { clone as cloneSkeleton } from 'three/addons/utils/SkeletonUtils.js';

const loader = new GLTFLoader();
const cache = new Map<string, THREE.Group>();
/** Which cached roots actually contain a `SkinnedMesh` - computed once at cache time (character-animation-runtime.md section 3.1). */
const skinnedCache = new Set<string>();

/** True if any descendant (including `root` itself) is a `SkinnedMesh`. */
function containsSkinnedMesh(root: THREE.Object3D): boolean {
  let found = false;
  root.traverse((o) => {
    if ((o as THREE.SkinnedMesh).isSkinnedMesh) found = true;
  });
  return found;
}

/**
 * Loads any GLB, scaled so its bounding-box height matches `targetHeight` and
 * bottom-aligned to y=0. Despite the module's name this isn't character-only
 * - base-view reuses it for NPC bodies and building models too, since it's
 * the same load-once/clone-per-use shape regardless of subject. Cached by
 * `url + targetHeight`, not just `url`: a second call with a different
 * height must not silently reuse the first call's scale.
 *
 * Cloning: plain `Object3D.clone()` does not correctly duplicate a
 * `SkinnedMesh`'s bone bindings (every clone would share/corrupt one
 * skeleton). `SkeletonUtils`'s `clone()` clones bones + skin correctly but
 * costs more, so it's used ONLY for models that actually contain a
 * `SkinnedMesh` (detected once, at cache time) - buildings/props/non-rigged
 * NPC bodies keep the cheap plain clone (character-animation-runtime.md
 * section 3.1).
 */
export function loadCharacterModel(url: string, targetHeight = 1.8): Promise<THREE.Group> {
  return loadModel(url, targetHeight);
}

/**
 * Loads a GLB at the scale it was AUTHORED at, normalising nothing.
 *
 * For the files that carry their real-world size in the asset itself — every
 * `public/models/weapons/*.glb` does, as a root-node scale, which
 * `weapon-scale.test.ts` exists to defend — height-normalising is destructive:
 * it makes a 0.24 m pistol and a 1.45 m hockey stick the same size, and the
 * only place that difference is recorded is the thing being overwritten.
 *
 * Nothing is re-seated on y=0 either. A caller that wants the model centred
 * has to say so, because "authored" is the whole point.
 */
export function loadModelAtAuthoredScale(url: string): Promise<THREE.Group> {
  return loadModel(url, null);
}

/** `targetHeight` of null keeps the file's own scale and position — see `loadModelAtAuthoredScale`. */
function loadModel(url: string, targetHeight: number | null): Promise<THREE.Group> {
  const cacheKey = `${url} ${targetHeight ?? 'authored'}`;
  const cached = cache.get(cacheKey);
  if (cached) {
    const clone = skinnedCache.has(cacheKey) ? cloneSkeleton(cached) : cached.clone();
    return Promise.resolve(clone as THREE.Group);
  }

  return new Promise((resolve, reject) => {
    loader.load(
      url,
      (gltf) => {
        const root = gltf.scene;

        if (targetHeight !== null) {
          const box = new THREE.Box3().setFromObject(root);
          const size = box.getSize(new THREE.Vector3());
          const scale = targetHeight / size.y;
          root.scale.setScalar(scale);

          box.setFromObject(root);
          root.position.y -= box.min.y;
        }

        cache.set(cacheKey, root);
        if (containsSkinnedMesh(root)) {
          skinnedCache.add(cacheKey);
          resolve(cloneSkeleton(root) as THREE.Group);
        } else {
          resolve(root.clone());
        }
      },
      undefined,
      (err) => reject(err),
    );
  });
}

export function disposeModel(obj: THREE.Object3D): void {
  obj.traverse((child) => {
    const mesh = child as THREE.Mesh;
    if (mesh.geometry) mesh.geometry.dispose();
    const mat = mesh.material as THREE.Material | THREE.Material[] | undefined;
    if (!mat) return;
    const mats = Array.isArray(mat) ? mat : [mat];
    for (const m of mats) {
      const std = m as THREE.MeshStandardMaterial;
      std.map?.dispose();
      std.normalMap?.dispose();
      std.roughnessMap?.dispose();
      std.metalnessMap?.dispose();
      std.emissiveMap?.dispose();
      std.aoMap?.dispose();
      m.dispose();
    }
  });
}
